/**
 * The pre-run readiness checklist: one row per gate, ticked or not, with an optional figure under
 * the label.
 *
 * Extracted from `PreSolveState` so the company step shows the same list rather than a paragraph of
 * its own. Both steps answer the same question before a run — is there a pool, are the criteria
 * complete — and a reader who has learned to look for the ticks on one screen should find them on
 * the other. Only the NOUNS differ, and those arrive as `label` / `hint` from the caller.
 *
 * Deliberately dumb: no readiness logic lives here. Whether a row is ticked is the step's own
 * decision, because the two steps gate on different things (seat arithmetic on the person side, an
 * empty pool that is merely UNREAD on the company side) and a shared `ok` rule would have to know
 * about both.
 */
export interface ReadinessItem {
  label: string;
  ok: boolean;
  hint?: string;
}

export function ReadinessList({ id, items }: { id?: string; items: readonly ReadinessItem[] }) {
  return (
    <ul id={id} className="mt-4 space-y-1.5">
      {items.map((it) => (
        <li key={it.label} className="flex items-baseline gap-3">
          <span
            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-smycles-micro ${
              it.ok ? "bg-emerald-500/15 text-emerald-700" : "bg-ink/[0.06] text-ink-muted"
            }`}
            aria-hidden
          >
            {it.ok ? "✓" : "○"}
          </span>
          <div className="flex-1">
            <div className="text-smycles-meta">{it.label}</div>
            {it.hint && <div className="text-smycles-label text-ink-muted">{it.hint}</div>}
          </div>
        </li>
      ))}
    </ul>
  );
}
